import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import React, { useEffect, useState } from 'react';
import {
    generatePath,
    useHistory,
    useLocation,
    useParams,
} from 'react-router-dom';
import { fromJS } from 'immutable';

import {
    fetchExtensions,
    sendNumberVerification,
} from 'flow/actions/extension-actions';
import { fetchQueue, updateQueueMember } from 'flow/actions/pbx-actions';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import Button from 'flow/components/ui-components/Button';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import Loading from 'flow/components/ui-components/Loading';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useCustomer from 'flow/hooks/useCustomer';
import useExtension from 'flow/hooks/useExtension';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectCustomerProductTypeIsBundle } from 'flow/selectors/customer-selectors';
import { selectQueueMembers } from 'flow/selectors/queue-selectors';
import { PbxParams } from 'flow/types/Params';
import { QueueMember } from 'flow/types/Queue';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { extractName } from 'flow/utils';
import { Capabilities } from 'flow/utils/capability-utils';
import {
    addGeneralLogEntry,
    addSupervisorLogEntry,
    translateNumber,
} from 'flow/utils/browser-utils';
import { getExtensionHasCapability } from 'flow/utils/extension-utils-TW';
import { isFlowFreeUser } from 'flow/utils/flow-utils';
import { twMerge } from 'tailwind-merge';

const ParentPage = {
    overview: 'overview',
    queue: 'queue',
    settings: 'settings',
};

const PbxQueueMember = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { ownCustomer } = useCustomer();
    const isBundleCustomer = useAppSelector(selectCustomerProductTypeIsBundle);
    const { pathname } = useLocation();
    const { openModal } = useModal();
    const { queueKey } = useParams<PbxParams>();
    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );
    const queueMembers = useAppSelector((state) =>
        selectQueueMembers(state, queueKey),
    );
    const queueWaves = useAppSelector((state) =>
        state.pbx.getIn(['queues', queueKey, 'queueWaves']),
    );
    const queueDescription = useAppSelector((state) =>
        state.pbx.getIn(['queues', queueKey, 'description']),
    );
    const editable = useAppSelector((state) =>
        state.pbx.getIn(['queues', queueKey, 'editable']),
    );
    const statuses = useAppSelector((state) =>
        state.extensions.get('statuses'),
    );

    const [member, setMember] = useState<TypedMap<QueueMember>>();

    useEffect(() => {
        dispatch(fetchExtensions('contact', 'settings', 'statistsics'));
        dispatch(fetchQueue(queueKey));
    }, [dispatch, queueKey]);

    if (!queueMembers) {
        return <Loading />;
    }

    const addLogEntry = (logString: string) => {
        if (currentParentPage() === ParentPage.overview) {
            addSupervisorLogEntry(logString);
        } else {
            addGeneralLogEntry(logString);
        }
    };

    const handleToggle = (key: string, loggedIn: boolean) => {
        dispatch(
            updateQueueMember(
                queueKey,
                fromJS({
                    key: key,
                    loggedIn,
                }),
            ),
        );

        addLogEntry(
            `[Queue members] ${loggedIn ? 'Login' : 'Logout'} member clicked`,
        );
    };

    const handleSendVerification = (queueMember: TypedMap<QueueMember>) => {
        dispatch(
            sendNumberVerification(
                queueMember.get('extensionKey'),
                queueMember.getIn(['contact', 'mobile']),
            ),
        );
        addLogEntry('[Queue members] Send verification clicked');
    };

    const openDialog = (member: TypedMap<QueueMember>) => {
        openModal('confirmationDialog', {
            title: i18next.t('QueueSettings.Members.VerifyNumber.Title'),
            description: i18next.t(
                'QueueSettings.Members.VerifyNumber.Message',
                {
                    name: extractName(member?.get('contact')),
                    number: member?.getIn(['contact', 'mobile', 'number']),
                },
            ),
            confirmButtonText: i18next.t(
                'QueueSettings.Members.VerifyNumber.SendSMS',
            ),
            handleAccept: () => handleSendVerification(member),
        });
        setMember(member);
    };

    const loginQueueMember = (
        member: TypedMap<QueueMember>,
        entityKey?: string,
    ) => {
        /**
         * Flow Free Users need to have a verified number to log into queues.
         * For non-Flow Free users it's enough that they have a licence.
         */
        const memberCanBeLoggedInToQueue =
            isVerified(member) &&
            member.get('editable') &&
            (ownExtension.get('admin') ||
                ownExtensionKey === member.get('extensionKey'));

        return (
            <SettingRow
                key={`queue-member-${member.get('key')}`}
                cta={
                    <div
                        key={`togglekey-${entityKey ?? member.get('key')}`}
                        className="flex items-center gap-2"
                    >
                        <Toggle
                            value={member.get('loggedIn')}
                            disabled={!memberCanBeLoggedInToQueue}
                            onChange={(value: boolean) =>
                                handleToggle(member.get('key'), value)
                            }
                            activeColor="bg-pbx-huntgroup"
                            text={extractName(
                                member.get('contact'),
                                i18next.t('status.unknown'),
                            )}
                        />

                        {member.get('editable') && !isVerified(member) ? (
                            <IconButton
                                aria-label={i18next.t(
                                    'QueueSettings.Members.VerifyNumber.Title',
                                )}
                                onClick={() => openDialog(member)}
                                disabled={!member.get('editable')}
                            >
                                <Icon iconKey="error" />
                            </IconButton>
                        ) : null}
                    </div>
                }
                className="h-12"
            >
                <ContactAvatar
                    contact={member.get('contact')}
                    fallBackBackgroundColorClass={twMerge(
                        'group-hover:bg-gray-300 bg-gray-200',
                    )}
                    size="small"
                    status={statuses.get(member.get('extensionKey'))}
                />

                <div
                    className={twMerge(
                        'flex gap-1 truncate',
                        member.get('loggedIn')
                            ? 'text-gray-800'
                            : 'text-gray-400',
                    )}
                >
                    {extractName(
                        member.get('contact'),
                        i18next.t('status.unknown'),
                    )}
                    {member.get('extensionKey') === ownExtensionKey && (
                        <div
                            className={
                                member.get('loggedIn')
                                    ? 'text-gray-500'
                                    : 'text-gray-400'
                            }
                        >
                            ({i18next.t('chat.you')})
                        </div>
                    )}
                </div>
            </SettingRow>
        );
    };

    const memberExtensionKeys = queueMembers.map(
        (member: TypedMap<QueueMember>) => member.get('extensionKey'),
    );

    const filteredExtensions = extensions.filter((extension) =>
        memberExtensionKeys.contains(extension.get('key')),
    );

    const isVerified = (member: TypedMap<QueueMember>) => {
        if (!ownCustomer || !filteredExtensions || !member) {
            return false;
        }

        const extension = filteredExtensions.get(member.get('extensionKey'));

        /*
         * If no extension is available in the lists we can not verify either licence or verification of number.
         * This can cause errors for hidden contacts, just pass them as true so no verification is needed.
         */
        if (!extension) {
            return true;
        }

        if (!getExtensionHasCapability(extension, Capabilities.SMS)) {
            return true;
        }

        return (
            (extension.hasIn(['usage', 'verifiedMobileNumber']) &&
                isFlowFreeUser(ownCustomer, extension)) ||
            extension.has('license') ||
            isBundleCustomer
        );
    };

    const currentParentPage = () => {
        if (
            pathname.startsWith(
                generatePath(routes['pbx.overview'], {
                    entityKey: queueKey,
                }),
            )
        ) {
            return ParentPage.overview;
        }

        if (
            pathname.startsWith(
                generatePath(routes['pbx.queues.entityKey.settings'], {
                    entityKey: queueKey,
                }),
            )
        ) {
            return ParentPage.settings;
        }

        return ParentPage.queue;
    };

    const handleBack = () => {
        const backPage: Record<string, string> = {
            overview: routes['pbx.overview'],
            queue: routes['pbx.queues.entityKey'],
            settings: routes['pbx.queues.entityKey.settings'],
        };

        history.push(
            generatePath(backPage[currentParentPage()], {
                entityKey: queueKey,
            }),
        );
    };

    const navigateToEditMembers = () => {
        const editPage: Record<string, string> = {
            overview: routes['pbx.overview.queueKey.members.edit'],
            queue: routes['pbx.queues.queueKey.members.edit'],
            settings: routes['pbx.queues.queueKey.settings.members.edit'],
        };

        history.push(
            generatePath(editPage[currentParentPage()], {
                queueKey,
            }),
        );
    };

    return (
        <Page
            header={
                currentParentPage() === ParentPage.overview
                    ? `${i18next.t(
                          'Queue.HandleMembers.Title',
                      )} - ${queueDescription}`
                    : 'Queue.HandleMembers.Title'
            }
            onClickBack={handleBack}
            action={
                editable && ownExtension.get('admin') ? (
                    <IconButton
                        onClick={() =>
                            openModal('addQueueMember', { queueKey })
                        }
                        tooltipLabel={i18next.t('chatsettings.add_member')}
                    >
                        <Icon iconKey="person_add" />
                    </IconButton>
                ) : null
            }
        >
            {queueWaves?.size > 0
                ? queueWaves.map((queueWave) => (
                      <div key={`wrapper-${queueWave.get('key')}`}>
                          <div
                              className={twMerge(
                                  'flex items-center justify-between px-2 py-5',
                                  queueWave.get('key') !== 'queuewave-0'
                                      ? 'mt-6'
                                      : null,
                              )}
                          >
                              <h2 className="text-xl/5 font-bold">
                                  {queueWave.get('key') === 'queuewave-0'
                                      ? i18next.t(
                                            'Queue.HandleMembers.DelayedHuntGroup.Default',
                                        )
                                      : i18next.t(
                                            'Queue.HandleMembers.DelayedHuntGroup',
                                        )}
                              </h2>

                              {queueWave.get('key') !== 'queuewave-0' ? (
                                  <div className="text-sm">
                                      {i18next.t(
                                          'Queue.HandleMembers.DelayedHuntGroup.After',
                                      )}
                                      &nbsp;
                                      <span className="font-bold">
                                          {`${translateNumber(
                                              (queueWave.get('delay') -
                                                  (queueWave.get('delay') %
                                                      60)) /
                                                  60,
                                          )} min ${translateNumber(
                                              queueWave.get('delay') % 60,
                                          )} sec`}
                                      </span>
                                  </div>
                              ) : null}
                          </div>
                          {queueWave
                              .get('queueWaveMappingDTOs')
                              .map((queueWaveMapping) =>
                                  loginQueueMember(
                                      queueWaveMapping.get('queueMember'),
                                      queueWaveMapping.get('key'),
                                  ),
                              )}
                      </div>
                  ))
                : queueMembers.map((queueMember) =>
                      loginQueueMember(queueMember),
                  )}

            {!queueMembers.isEmpty() ? <Divider className="my-6" /> : null}

            {ownExtension.get('admin') ? (
                <div className="flex pb-3">
                    <Button
                        className="px-5"
                        color="primary"
                        onClick={navigateToEditMembers}
                        disabled={queueMembers.isEmpty()}
                    >
                        {i18next.t('pbx.edit_members')}
                    </Button>
                </div>
            ) : null}
        </Page>
    );
};

export default PbxQueueMember;
